import { useState, useEffect, useCallback } from 'react'
import { Loader2, Printer, Clock, Lock, Unlock } from 'lucide-react'
import api from '../../core/services/api'

interface Props {
  commessaId: string
  onToast: (msg: string, type: 'success' | 'error') => void
}

interface Personale {
  id: string
  nome: string
  cognome: string
}

interface RiepilogoRow {
  personale_id: string
  personale_nome?: string
  commessa_id?: string
  commessa_codice?: string
  fascicolo_id?: string
  fascicolo_codice?: string
  ore_totali: number
  ore_locked: number
  ore_libere: number
  periodo_da?: string
  periodo_a?: string
}

export default function RiepilogoOreTab({ commessaId, onToast }: Props) {
  const [personaleList, setPersonaleList] = useState<Personale[]>([])
  const [riepilogo, setRiepilogo] = useState<RiepilogoRow[]>([])
  const [loading, setLoading] = useState(false)

  // Filters
  const [filterPersonale, setFilterPersonale] = useState('')
  const [filterDataDa, setFilterDataDa] = useState('')
  const [filterDataA, setFilterDataA] = useState('')

  useEffect(() => {
    loadBaseData()
  }, [])

  const loadBaseData = async () => {
    try {
      const persRes = await api.get('/v1/personale')
      setPersonaleList(persRes.data)
    } catch {
      onToast('Errore nel caricamento dati base', 'error')
    }
  }

  const loadRiepilogo = useCallback(async () => {
    try {
      setLoading(true)
      const params: Record<string, string> = { commessa_id: commessaId }
      if (filterPersonale) params.personale_id = filterPersonale
      if (filterDataDa) params.data_da = filterDataDa
      if (filterDataA) params.data_a = filterDataA

      const res = await api.get('/v1/ore/riepilogo', { params })
      setRiepilogo(Array.isArray(res.data) ? res.data : [])
    } catch {
      onToast('Errore nel caricamento riepilogo', 'error')
    } finally {
      setLoading(false)
    }
  }, [commessaId, filterPersonale, filterDataDa, filterDataA, onToast])

  useEffect(() => {
    loadRiepilogo()
  }, [loadRiepilogo])

  const getPersonaleNome = (id: string) => {
    const p = personaleList.find(x => x.id === id)
    return p ? `${p.cognome} ${p.nome}` : id
  }

  // Totals
  const totaleOre = riepilogo.reduce((s, r) => s + r.ore_totali, 0)
  const totaleLocked = riepilogo.reduce((s, r) => s + r.ore_locked, 0)
  const totaleLibere = riepilogo.reduce((s, r) => s + r.ore_libere, 0)

  const handlePrint = () => {
    window.print()
  }

  return (
    <div>
      {/* Action bar */}
      <div className="flex items-center justify-end mb-4">
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Printer className="w-4 h-4" />
          Stampa
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Dipendente</label>
            <select
              value={filterPersonale}
              onChange={e => setFilterPersonale(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Tutti</option>
              {personaleList.map(p => (
                <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Da</label>
            <input
              type="date"
              value={filterDataDa}
              onChange={e => setFilterDataDa(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">A</label>
            <input
              type="date"
              value={filterDataA}
              onChange={e => setFilterDataA(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Totale Ore</p>
              <p className="text-2xl font-bold text-gray-900">{totaleOre.toFixed(1)}h</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-lg">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Ore Bloccate (SAL)</p>
              <p className="text-2xl font-bold text-amber-700">{totaleLocked.toFixed(1)}h</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-100 rounded-lg">
              <Unlock className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Ore Libere</p>
              <p className="text-2xl font-bold text-green-700">{totaleLibere.toFixed(1)}h</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : riepilogo.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p>Nessun dato trovato per i filtri selezionati</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Dipendente</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Ore Totali</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Ore Bloccate</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Ore Libere</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {riepilogo.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {row.personale_nome || getPersonaleNome(row.personale_id)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">{row.ore_totali.toFixed(1)}h</td>
                    <td className="px-4 py-3 text-right">
                      {row.ore_locked > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <Lock className="w-3 h-3" /> {row.ore_locked.toFixed(1)}h
                        </span>
                      ) : (
                        <span className="text-gray-400">0h</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-green-700 font-medium">{row.ore_libere.toFixed(1)}h</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-4 py-3 text-gray-700">Totale</td>
                  <td className="px-4 py-3 text-right text-gray-800">{totaleOre.toFixed(1)}h</td>
                  <td className="px-4 py-3 text-right text-amber-700">{totaleLocked.toFixed(1)}h</td>
                  <td className="px-4 py-3 text-right text-green-700">{totaleLibere.toFixed(1)}h</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
